import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useCallback, useMemo } from 'react';
import { apiTokenRequest, loginRequest } from './msalConfig';
import { RedirectInProgressError, isInteractionInProgress } from './redirectInProgress';
import { AccessDeniedError, isEntitlementRefusal, wasAccessDeniedAtRedirect } from './accessDenied';
import { needsInteraction } from './needsInteraction';
import { signIn, signOut, startReauth } from './authSession';

export function useAuth() {
  // #14687 — inProgress guards against issuing a second top-level navigation.
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0] ?? null;

  // #24685 — sign-in and sign-out go through the single-flight helpers: one redirect per page, a
  // stuck interaction_in_progress marker cleared (sign-in retried once), the active account named on
  // sign-out, and no promise left to reject unhandled.
  const login = useCallback(() => {
    void signIn(instance, loginRequest);
  }, [instance]);

  const logout = useCallback(() => {
    void signOut(instance, account);
  }, [instance, account]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) throw new Error('No authenticated account');
    // #19758 — Entra already refused this account's API token on the way back from a redirect
    // (msalInstance.ts). Asking again would only redirect again: the infinite loop.
    if (wasAccessDeniedAtRedirect()) throw new AccessDeniedError();
    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      // #19758 — AADSTS50105: the account is not assigned to the API app. That is "no entitlement
      // at all", not "sign in again" — checked BEFORE the interaction-required branch, because
      // Entra reports it as interaction-required and answering it with acquireTokenRedirect is
      // exactly the loop. Every other interaction-required error keeps the redirect below.
      if (isEntitlementRefusal(error)) throw new AccessDeniedError();
      // #24685 — `needsInteraction` also covers the BrowserAuthError `timed_out` (and the other
      // codes) silent renewal fails with after a long idle; those used to be rethrown unhandled.
      if (!needsInteraction(error)) throw error;
      // #14687 — throw rather than `return ''`: an empty string is indistinguishable from a real
      // token, so the caller fetched with an empty bearer and 401'd instead of simply letting the
      // redirect complete.
      if (isInteractionInProgress(inProgress)) throw new RedirectInProgressError();
      // #24685 — single-flight: however many callers fail together, exactly one redirect starts,
      // back to this page as this account. 'suppressed' = a re-auth ran moments ago without curing
      // it; another redirect would loop, so the original failure is surfaced.
      if (startReauth(instance, account, apiTokenRequest) === 'suppressed') throw error;
      throw new RedirectInProgressError();
    }
  }, [instance, account, inProgress]);

  const roles = useMemo<string[]>(() => {
    if (!account?.idTokenClaims) return [];
    const claims = account.idTokenClaims as Record<string, unknown>;
    const r = claims['roles'];
    return Array.isArray(r) ? r : [];
  }, [account]);

  const displayName = account?.name ?? account?.username ?? '';
  const email = account?.username ?? '';
  const entraId = account?.localAccountId ?? '';

  return {
    isAuthenticated,
    account,
    displayName,
    email,
    entraId,
    roles,
    login,
    logout,
    getAccessToken,
  };
}
